import type {
  PublicWorkEnvironmentSurvey,
  Questionnaire,
  QuestionnaireFactor,
  SubmitWorkEnvironmentSurveyRequest,
} from '../../api/workEnvironmentTypes';

/** The answer to each question so far, by question reference */
export type Answers = Record<string, number>;

/** The app speaks Norwegian or English. The questions default to the same, in bokmål for Norwegian. */
export const questionLanguageFor = (appLanguage: string) => (appLanguage.startsWith('en') ? 'en' : 'nb');

/** An id the error summary can link to. References contain characters that do not belong in an id. */
export const questionElementId = (ref: string) => `question-${ref.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const refsOf = (factor: QuestionnaireFactor) => factor.questions.map((question) => question.ref);

/** The questions in a part that still need an answer. A skipped part needs none. */
export const missingInPart = (factor: QuestionnaireFactor, answers: Answers, skipped: string[]) =>
  skipped.includes(factor.key) ? [] : factor.questions.filter((question) => answers[question.ref] === undefined);

export const isPartDone = (factor: QuestionnaireFactor, answers: Answers, skipped: string[]) =>
  missingInPart(factor, answers, skipped).length === 0;

export const questionCount = (questionnaire: Questionnaire) => questionnaire.factors.flatMap(refsOf).length;

export const answeredCount = (questionnaire: Questionnaire, answers: Answers) =>
  questionnaire.factors.flatMap(refsOf).filter((ref) => answers[ref] !== undefined).length;

/** What is sent: answers in the parts the employee answered, and the parts they chose to skip */
export const surveyResponse = (
  uuid: string,
  questionnaire: Questionnaire,
  answers: Answers,
  skipped: string[],
): SubmitWorkEnvironmentSurveyRequest => ({
  uuid,
  answers: questionnaire.factors
    .filter((factor) => !skipped.includes(factor.key))
    .flatMap(refsOf)
    .filter((ref) => answers[ref] !== undefined)
    .map((ref) => ({ ref, value: answers[ref] })),
  skipped: questionnaire.factors
    .filter((factor) => factor.skippable && skipped.includes(factor.key))
    .map((factor) => factor.key),
});

export type ReportingChannels = Pick<
  PublicWorkEnvironmentSurvey,
  'whistleblowingUrl' | 'followUpRoutineUrl' | 'hasSafetyRep' | 'safetyReps' | 'safetyRepTakenOver'
>;

/** «Kari Nordmann (kari@firma.no)», or the email alone when the person has given no name */
export const contactText = ({ name, email }: { name: string | null; email: string }) =>
  name ? `${name} (${email})` : email;

/** Who the employee can speak to: the team's safety representative, or the main one who took over. Null without any. */
export const contactKey = (channels: ReportingChannels) =>
  (channels.safetyReps ?? []).length === 0 ? null : channels.safetyRepTakenOver ? 'contactTakenOver' : 'contact';

/**
 * How the employee can report instead of answering. Without a whistleblowing link from HR, they
 * are pointed to the organisation's own routines. Without a safety representative, to HR.
 */
export const sensitiveTextKey = (skippable: boolean, channels: ReportingChannels) =>
  `${skippable ? 'skippable' : 'report'}${channels.whistleblowingUrl ? '' : 'NoLink'}${
    channels.hasSafetyRep ? '' : 'NoRep'
  }` as const;

/** Who sees the result of the team */
export const whoSeesKey = (channels: ReportingChannels) => (channels.hasSafetyRep ? 'who' : 'whoNoRep');
